import React, { useState, useEffect } from 'react';

import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import FormContainer from '../../../FormContainer';
import Table from '../../../../Table';
import Buttons from './Buttons';
import BillPaymentsCol from './BillPaymentsCol';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import BillInsurancesTable from './BillInsurancesTable';
import ActionsMenu from '../../../../ActionsMenu';
import capitalize from '../../../../../helpers/capitalize';

import BillService from '../../../../../services/BillService';
import BillLineItemService from '../../../../../services/BillLineItemService';
import useNewBillForm from '../../../../../forms/loan/newBill';
import useBillLineItemForm from '../../../../../forms/loan/billLineItem';
import { useUiContext } from '../../../../../context/UiContext';
import sumDollarAmounts from '../../../../../helpers/sumDollarAmounts';
import getBillBadgeColor from '../../../../../helpers/tables/getBillBadgeColor';
import useBillActions from './hooks/useBillActions';

const tabs = [
  {
    label: 'Bills',
    activeOn: 'Bill',
  },
  {
    label: 'Bill Payments',
  },
];

function PaymentIineItemInfo({ row }) {
  if (!row._payment_line_item) {
    return 'This bill line item is not connected with a line item';
  }

  return (
    <ul className="list-disc pl-3">
      <li>{row._payment_line_item._payment.date}</li>
      <li>{row._payment_line_item._line_item._invoice.pixel_invoice_number}</li>
      <li>{row._payment_line_item._line_item.description}</li>
    </ul>
  );
}

function parseToFloat(numberString) {
  // Remove commas
  const sanitizedString = numberString.toString().replace(/,/g, '');
  // Convert to float
  const floatNumber = parseFloat(sanitizedString);
  return floatNumber;
}

export default function Bill({ loanId, refetchLoan, loan, isLender }) {
  const [deleteBillLineItemId, setDeleteBillLineItemId] = useState(null);
  const [editingBillLineItemId, setEditingBillLineItemId] = useState(null);
  const [editingBillLineItemDescription, setEditingBillLineItemDescription] =
    useState(null);
  const [editingBillLineItemAmount, setEditingBillLineItemAmount] =
    useState(null);

  const { billId } = useParams();

  const { showToast } = useUiContext();

  const {
    data: bill,
    refetch,
    isFetching,
    fetchStatus,
    isFetched,
  } = useQuery({
    queryKey: ['bill', billId],
    queryFn: () => BillService.getBill(billId),
  });

  const isBillPayed = bill?.status === 'paid';

  useEffect(() => {
    if (fetchStatus === 'idle') {
      //   setDeleteBillLineItemId(null);
      setEditingBillLineItemId(null);
      setEditingBillLineItemDescription(null);
      setEditingBillLineItemAmount(null);
    }
  }, [fetchStatus]);

  const { mutate: updateBill, isPending } = useMutation({
    mutationFn: (formValues) =>
      BillService.updateBill({
        formValues,
        loanId,
        type: formValues.type,
        billId,
      }),
    onSuccess: () => {
      showToast({
        text: 'Bill updated successfully',
        type: 'success',
      });
      refetch();
      refetchLoan();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: createBillLineItem, isPending: isPendingLineItem } =
    useMutation({
      mutationFn: (formValues) =>
        BillLineItemService.createBillLineItem({ formValues, billId }),
      onSuccess: () => {
        showToast({
          text: 'Bill line item created successfully',
          type: 'success',
        });
        refetch();
        refetchLoan();
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    });

  const { mutate: deleteBillLineItem } = useMutation({
    mutationFn: (billLineItemId) => {
      setDeleteBillLineItemId(billLineItemId);
      return BillLineItemService.deleteBillLineItem(billLineItemId);
    },
    onSuccess: () => {
      showToast({
        text: 'Bill line item deleted successfully',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: updateBillLineItem, isPending: isBillLineItemUpdating } =
    useMutation({
      mutationFn: () =>
        BillLineItemService.updateBillLineItem({
          formValues: {
            description: editingBillLineItemDescription,
            amount: parseToFloat(editingBillLineItemAmount),
            bill_id: billId,
            payment_line_item_id: bill?._bill_line_items.find(
              (item) => item.id === editingBillLineItemId
            )?.payment_line_item_id,
          },
          billLineItemId: editingBillLineItemId,
        }),
      onSuccess: () => {
        showToast({
          text: 'Bill line item updated successfully',
          type: 'success',
        });
        refetch();
        refetchLoan();
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    });

  const cols = [
    {
      title: 'Description',
      keys: ['description'],
      type: 'editable',
      isEditable: (row) => row.id === editingBillLineItemId && !isBillPayed,
      inputType: 'text',
      onTdClick: (event, row) => {
        setEditingBillLineItemId(row.id);
        setTimeout(() => {
          const input = document.querySelector(
            `td[data-row-id="${row.id}"] input`
          );

          if (input) {
            input.focus();
          }
        }, 100);
      },
      onChange: (_, value) => setEditingBillLineItemDescription(value),
    },
    {
      title: 'Amount',
      keys: ['amount'],
      type: 'editable',
      isEditable: (row) => row.id === editingBillLineItemId && !isBillPayed,
      inputType: 'currency',
      // eslint-disable-next-line react/no-unstable-nested-components
      tooltip: (row) => {
        return row.id === 'total' ? null : <PaymentIineItemInfo row={row} />;
      },
      fixedTooltip: true,
      onChange: (_, value) => setEditingBillLineItemAmount(value),
      onTdClick: (event, row) => {
        setEditingBillLineItemId(row.id);
        setTimeout(() => {
          const input = document.querySelector(
            `td[data-row-id="${row.id}"][data-value="${row.amount}"] input`
          );

          if (input) {
            input.focus();
          }
        }, 100);
      },
    },
    {
      type: 'component',
      component: Buttons,
      props: (row) => ({
        row,
        deleteBillLineItemId,
        editingBillLineItemId,
        isFetching,
        isBillLineItemUpdating,
        isBillPayed,
        editingBillLineItemAmount,
        editingBillLineItemDescription,
        updateBillLineItem,
        deleteBillLineItem,
        setEditingBillLineItemId,
      }),
    },
    {
      title: 'Status',
      keys: ['status'],
    },
    {
      title: 'Bill Payments',
      keys: ['_bill_payment.pixel_number'],
      type: 'component',
      component: BillPaymentsCol,
      props: (row) => ({
        row,
        bill,
        loanId,
        refetch,
        lineItems: bill?._bill_line_items,
        isLender,
      }),
    },
  ];

  const currentCols = isLender
    ? cols.filter((col) => col.type !== 'component')
    : cols;

  const billForm = useNewBillForm(
    updateBill,
    bill,
    loanId,
    false,
    loan?.borrowers,
    loan?._property_of_loan?.id
  );

  const lineItemForm = useBillLineItemForm(createBillLineItem);

  const lineItemsWithTotals = bill?._bill_line_items?.length
    ? [
        ...(bill?._bill_line_items ?? []),
        {
          id: 'total',
          description: 'Total',
          amount: sumDollarAmounts(bill?._bill_line_items, 'amount'),
          status: '',
          _bill_payment: {
            pixel_number: '312312',
          },
          style: 'font-semibold',
        },
      ]
    : [];

  const isInsurancesTableVisible = !!(
    (bill?._insurances_via_next_bill?.length && !bill?._insurances?.length) ||
    bill?._insurances?.length
  );

  const billActions = useBillActions({
    bill,
    isLender,
    refetchLoan,
    refetch,
    loanId,
    loan,
  });

  return (
    <div>
      <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2 h-[37px]">
        <AddOrEditPageTabs tabs={tabs} />
      </div>
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        <div className="mb-10">
          <FormContainer
            readOnly={isLender}
            noSubmit={isBillPayed || isLender}
            form={billForm}
            tabindex="0"
            isSubmitting={isPending}
            disabled={!billForm.formik.dirty}
            headerRight={
              <ActionsMenu
                actions={billActions}
                badgeColor={getBillBadgeColor(bill?.status)}
                badgeText={capitalize(bill?.status)}
              />
            }
          />
        </div>
        <div className="mb-10">
          <Table
            cols={currentCols}
            rows={lineItemsWithTotals}
            title="Line Items"
          />
        </div>
        {!isBillPayed && isFetched && !isLender && (
          <FormContainer
            form={lineItemForm}
            isSubmitting={isPendingLineItem}
            noBillComId
            disabled={!lineItemForm.formik.dirty}
            groupSizeOffset={1}
          />
        )}
        {isInsurancesTableVisible && (
          <div className="mt-10">
            <BillInsurancesTable bill={bill} />
          </div>
        )}
      </div>
    </div>
  );
}
